import dayjs from 'dayjs';
import { CoachConversationSummary, readyCoachConversationTitle } from '../../api/coachAPI';

/**
 * The buckets the Chat History list is split into, newest first. `thisWeek`
 * covers the rest of the last seven days and `thisMonth` the rest of the last
 * thirty; anything older falls into `older`.
 */
export type CoachHistoryGroup = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'older';

export const COACH_HISTORY_GROUP_ORDER: CoachHistoryGroup[] = [
  'today',
  'yesterday',
  'thisWeek',
  'thisMonth',
  'older',
];

export interface CoachHistoryItem {
  conversationId: string;
  /**
   * The conversation's name, as generated by the gateway, or null while that
   * name is still being generated - in which case the row falls back to the
   * numbered placeholder built from `titleNumber`.
   */
  title: string | null;
  /**
   * Fallback title number, used only until the gateway has named the
   * conversation. Conversations are numbered in the order they were started, so
   * "Conversation #1" is the member's oldest chat and the number never shifts as
   * new chats are added.
   */
  titleNumber: number;
  /**
   * Translation key for the leading word of the date line ("Today",
   * "Yesterday"), or null when `dateLabel` already reads as a whole label.
   */
  datePrefixKey: string | null;
  // The date part of the subtitle, e.g. "Aug 12", "Monday - Aug 10".
  dateLabel: string;
  group: CoachHistoryGroup;
}

export interface CoachHistorySection {
  group: CoachHistoryGroup;
  items: CoachHistoryItem[];
}

const DAY_MONTH = 'MMM D';
const DAY_MONTH_YEAR = 'MMM D, YYYY';

/**
 * Buckets a conversation's last-activity date relative to `now`.
 */
export function coachHistoryGroupFor(lastMessageAt: string, now: dayjs.Dayjs): CoachHistoryGroup {
  const date = dayjs(lastMessageAt);
  const daysAgo = now.startOf('day').diff(date.startOf('day'), 'day');

  if (daysAgo <= 0) return 'today';
  if (daysAgo === 1) return 'yesterday';
  if (daysAgo < 7) return 'thisWeek';
  if (daysAgo < 30) return 'thisMonth';
  return 'older';
}

/**
 * Builds the date subtitle for a history item: "Today - Aug 12" for today,
 * "Yesterday - Aug 11", the weekday for the rest of the week ("Monday - Aug
 * 10"), and a plain date further back ("Jul 28", with the year once it is not
 * the current one).
 */
function dateLabelFor(
  lastMessageAt: string,
  group: CoachHistoryGroup,
  now: dayjs.Dayjs,
): Pick<CoachHistoryItem, 'datePrefixKey' | 'dateLabel'> {
  const date = dayjs(lastMessageAt);

  if (group === 'today') {
    return { datePrefixKey: 'coach.history.relative.today', dateLabel: date.format(DAY_MONTH) };
  }
  if (group === 'yesterday') {
    return { datePrefixKey: 'coach.history.relative.yesterday', dateLabel: date.format(DAY_MONTH) };
  }
  if (group === 'thisWeek') {
    return { datePrefixKey: null, dateLabel: date.format(`dddd - ${DAY_MONTH}`) };
  }

  const isSameYear = date.year() === now.year();
  return { datePrefixKey: null, dateLabel: date.format(isSameYear ? DAY_MONTH : DAY_MONTH_YEAR) };
}

/**
 * Numbers the conversations for their placeholder titles: oldest started is #1,
 * so a number never shifts as newer chats are added.
 */
export function buildCoachConversationNumbers(
  conversations: CoachConversationSummary[],
): Map<string, number> {
  const numberByConversationId = new Map<string, number>();
  [...conversations]
    .sort((a, b) => dayjs(a.startedAt).valueOf() - dayjs(b.startedAt).valueOf())
    .forEach((conversation, index) => {
      numberByConversationId.set(conversation.conversationId, index + 1);
    });
  return numberByConversationId;
}

/**
 * Turns the gateway's conversation list into the grouped, titled and sorted
 * shape the Chat History panel renders.
 *
 * Conversations are numbered by when they started (oldest first) so the
 * placeholder titles stay stable, but listed by when they were last active
 * (newest first) so the chat the member just left is at the top.
 */
export function buildCoachHistorySections(
  conversations: CoachConversationSummary[],
  now: dayjs.Dayjs = dayjs(),
): CoachHistorySection[] {
  const numberByConversationId = buildCoachConversationNumbers(conversations);

  const items: CoachHistoryItem[] = [...conversations]
    .sort((a, b) => dayjs(b.lastMessageAt).valueOf() - dayjs(a.lastMessageAt).valueOf())
    .map((conversation) => {
      const group = coachHistoryGroupFor(conversation.lastMessageAt, now);
      return {
        conversationId: conversation.conversationId,
        title: readyCoachConversationTitle(conversation) ?? null,
        titleNumber: numberByConversationId.get(conversation.conversationId) ?? 0,
        group,
        ...dateLabelFor(conversation.lastMessageAt, group, now),
      };
    });

  return COACH_HISTORY_GROUP_ORDER
    .map((group) => ({ group, items: items.filter((item) => item.group === group) }))
    .filter((section) => section.items.length > 0);
}
